import React from 'react';
import classNames from 'classnames/bind';
import { DateTime } from 'luxon';
import { DatesRangeValue } from '@mantine/dates';
import { MultiValue } from 'react-select';
import { Skeleton } from '@mantine/core';
import { useDebouncedValue } from '@tanstack/react-pacer';

import { Camera } from 'types/camera';
import { Stream } from 'types/stream';
import {
  FilesSearchEntity,
  ImageSearchType,
  useFilesSearch,
  UseFilesSearchParams,
} from 'files/hooks/useFilesSearch';
import { Gateway } from 'types/gateway';
import { FilesSearchResult } from 'types/files_search_result';
import { LumeoFileWithSearchMeta } from 'types/file_with_search_meta';
import {
  DATE_RANGE_PRESETS,
  RelativeDateRange,
} from 'components/DateRangePickerInput/time_range';

import { useCursorPagination } from 'components/Pagination/hooks/useCursorPagination';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useFilesCursorWithSearchMetadata } from 'files/hooks/useFilesWithSearchMetadata';
import { transformSearchQuery } from 'files/services/transformSearchQuery';
import { useModal } from 'hooks/useModal';
import { useStream } from 'streams/hooks/useStream';

import { Button } from 'components/Button/Button';
import { CursorPaginationProps } from 'components/Pagination/CursorPagination';
import { Accordion } from 'components/Accordion/Accordion';
import { CameraSelect } from 'cameras/components/CameraSelect/CameraSelect';
import {
  DateRange,
  DateTimeRangeInput,
} from 'components/DateTimeRangeInput/DateTimeRangeInput';
import { Drawer, DrawerProps } from 'components/Drawer/DrawerDeprecated';
import { Field } from 'components/Field/Field';
import { FilesSearchEntitiesInput } from 'files/components/FilesSearchEntitiesInput/FilesSearchEntitiesInput';
import { FilesSearchUpgradeNotice } from 'files/views/Search/UpgradeNotice';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Input } from 'components/Input/Input';
import { OptionalTooltip, Tooltip } from 'components/Tooltip/Tooltip';
import { Radio, RadioOption } from 'components/Radio/Radio';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';
import { TrackableList, TrackableItem } from 'types/trackables';
import { TrackableItemSelect } from 'trackables/components/TrackableItemSelect/TrackableItemSelect';
import { TrackableListSelect } from 'trackables/components/TrackableListSelect/TrackableListSelect';

import { FilesSearchConfidenceSlider } from '../FilesSearchConfidenceSlider/FilesSearchConfidenceSlider';
import { FilesSearchDetail } from './FilesSearchDetail';
import { FilesSearchDrawerList } from './FilesSearchDrawerList';
import styles from './FilesSearchDrawer.module.scss';

const c = classNames.bind(styles);

const limit = 150;
const DEFAULT_DATE_RANGE: RelativeDateRange = 'TODAY';
const DEFAULT_SEARCH_ENTITIES: FilesSearchEntity[] = [
  'trackables',
  'events',
  'objects',
  'texts',
  'vectors',
];

type BaseFilters = Omit<UseFilesSearchParams, 'query'> & {
  query?: string;
  search_entities: FilesSearchEntity[];
  trackable_list_ids?: string[];
  trackable_item_ids?: string[];
  sort_by?: 'relevance' | 'created_at';
  limit: number;
};

type FilesSearchDrawerProps = Pick<DrawerProps, 'open'> & {
  defaultFile?: Pick<LumeoFileWithSearchMeta, 'id' | 'created_at'>;
  defaultStreamId?: string;
  initialDateRange?: DateRange;
};

function getDefaultFileDateRange(createdAt: string): [Date, Date] {
  const anchorDate = DateTime.fromISO(createdAt);

  return [
    anchorDate.minus({ day: 3.5 }).toJSDate(),
    anchorDate.plus({ day: 3.5 }).toJSDate(),
  ];
}

function getResolvedDateRange({
  defaultFile,
  initialDateRange,
}: Pick<FilesSearchDrawerProps, 'defaultFile' | 'initialDateRange'>): DateRange {
  if (initialDateRange) {
    return initialDateRange;
  }

  if (defaultFile) {
    return getDefaultFileDateRange(defaultFile.created_at);
  }

  return DEFAULT_DATE_RANGE;
}

function getDatesFromDateRange(dateRange: DateRange): [Date, Date] {
  if (Array.isArray(dateRange)) {
    const [since, until] = dateRange;

    if (since && until) {
      return [since, until];
    }

    return DATE_RANGE_PRESETS[DEFAULT_DATE_RANGE]();
  }

  return DATE_RANGE_PRESETS[dateRange]();
}

export function FilesSearchDrawer({
  open,
  defaultFile,
  defaultStreamId,
  initialDateRange,
}: FilesSearchDrawerProps) {
  const { close } = useModal();

  return (
    <Drawer
      classNames={{
        root: c('drawer'),
        body: c('drawer-body'),
        content: c('drawer-content'),
      }}
      containerClassName={c('drawer-container')}
      open={open}
      onClose={() => close()}
    >
      <FilesSearchDrawerContent
        open={open}
        defaultFile={defaultFile}
        defaultStreamId={defaultStreamId}
        initialDateRange={initialDateRange}
      />
    </Drawer>
  );
}

function FilesSearchDrawerContent({
  open,
  defaultFile,
  defaultStreamId,
  initialDateRange,
}: FilesSearchDrawerProps) {
  const { data: currentPlan } = useCurrentPlan();
  const [dateRange, setDateRange] = React.useState<DateRange>(() =>
    getResolvedDateRange({ defaultFile, initialDateRange })
  );

  const [filters, setFilters] = React.useState<BaseFilters>(() => {
    const [since, until] = getDatesFromDateRange(
      getResolvedDateRange({ defaultFile, initialDateRange })
    );

    return {
      created_ts_since: since.toISOString(),
      created_ts_until: until.toISOString(),
      confidence: 0.5,
      search_entities: DEFAULT_SEARCH_ENTITIES,
      stream_ids: defaultStreamId ? [defaultStreamId] : undefined,
      sort_by: 'created_at',
      limit,
    };
  });

  const {
    cursorParams,
    getProps: getCursorPaginationProps,
    reset: resetCursorPagination,
  } = useCursorPagination({ limit });

  // Offset pagination state for search path
  const [searchPage, setSearchPage] = React.useState(1);

  const [imageSearchEmbedding, setImageSearchEmbedding] = React.useState<
    number[] | undefined
  >();
  const [imageSearchType, setImageSearchType] = React.useState<
    ImageSearchType | undefined
  >();
  const [imageSearchPreviewUrl, setImageSearchPreviewUrl] = React.useState<
    string | undefined
  >();

  const [selectedTrackableLists, setSelectedTrackableLists] =
    React.useState<TrackableList[]>([]);
  const [selectedTrackableItems, setSelectedTrackableItems] =
    React.useState<TrackableItem[]>([]);

  const hasSelectedTrackableLists = selectedTrackableLists.length > 0;

  const [debouncedFilters] = useDebouncedValue(filters, { wait: 200 });

  const combinedQuery = React.useMemo(() => {
    const segments: string[] = [];

    if (debouncedFilters.query?.trim()) {
      segments.push(transformSearchQuery(debouncedFilters.query));
    }

    if (debouncedFilters.trackable_item_ids?.length) {
      segments.push(debouncedFilters.trackable_item_ids.join(' '));
    } else if (debouncedFilters.trackable_list_ids?.length) {
      segments.push(debouncedFilters.trackable_list_ids.join(' '));
    }

    return segments.join(' ').trim();
  }, [debouncedFilters]);

  const searchParams = React.useMemo(() => {
    const { trackable_list_ids, trackable_item_ids, search_entities, ...rest } =
      debouncedFilters;
    return {
      ...rest,
      search_entities,
      query: combinedQuery || undefined,
      type: imageSearchType,
      embedding: imageSearchEmbedding,
    };
  }, [combinedQuery, debouncedFilters, imageSearchType, imageSearchEmbedding]);

  const filesFilters = React.useMemo(() => {
    const { trackable_list_ids, trackable_item_ids, search_entities, ...rest } =
      debouncedFilters;
    return rest;
  }, [debouncedFilters]);

  const isSearching = Boolean(searchParams.query || imageSearchEmbedding);

  const filesSearchQueryResult = useFilesSearch({
    params: {
      ...searchParams,
      pagination: 'offset' as const,
      page: searchPage,
      limit,
    },
    keepPreviousData: true,
    enabled: isSearching && currentPlan?.can_search,
  });

  const filesQueryResult = useFilesCursorWithSearchMetadata({
    params: {
      ...filesFilters,
      ...cursorParams,
      with_thumbnail: true,
      include_snapshots: false,
      include_search_metadata: currentPlan?.can_search,
      is_recording: true,
    },
    keepPreviousData: true,
    enabled: !isSearching,
  });

  const { data: stream, isInitialLoading: isLoadingStream } =
    useStream(defaultStreamId);

  const [selectedFileId, setSelectedFileId] = React.useState(
    defaultFile ? defaultFile.id : undefined
  );

  const resetPagination = React.useCallback(() => {
    resetCursorPagination();
    setSearchPage(1);
  }, [resetCursorPagination]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const nextDateRange = getResolvedDateRange({ defaultFile, initialDateRange });
    const [since, until] = getDatesFromDateRange(nextDateRange);

    setSelectedFileId(defaultFile?.id);
    setDateRange(nextDateRange);
    setFilters((prev) => ({
      ...prev,
      created_ts_since: since.toISOString(),
      created_ts_until: until.toISOString(),
      stream_ids: defaultStreamId ? [defaultStreamId] : prev.stream_ids,
    }));
    resetPagination();
  }, [defaultFile, defaultStreamId, initialDateRange, open, resetPagination]);

  function handleInputChange({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) {
    setFilters(({ query, ...previousFilters }) => {
      return value
        ? { ...previousFilters, query: value }
        : { ...previousFilters, sort_by: 'created_at' };
    });
    resetPagination();
  }

  function handleDateRangeChange(
    nextDateRange?: DatesRangeValue,
    preset?: string
  ) {
    if (!nextDateRange) {
      return;
    }

    const [since, until] = nextDateRange;

    if (!since || !until) {
      return;
    }

    setDateRange(
      preset && preset !== 'CUSTOM'
        ? (preset as RelativeDateRange)
        : [since, until]
    );
    setFilters((previousFilters) => ({
      ...previousFilters,
      created_ts_since: since.toISOString(),
      created_ts_until: until.toISOString(),
    }));
    resetPagination();
  }

  function handleConfidenceChange(confidence: number) {
    setFilters((previousFilters) => ({
      ...previousFilters,
      confidence,
    }));
    resetPagination();
  }

  function handleStreamsChange(streams: MultiValue<Stream>) {
    setFilters((previousFilters) => ({
      ...previousFilters,
      stream_ids: streams.map(({ id }) => id),
    }));
    resetPagination();
  }

  function handleCamerasChange(cameras: MultiValue<Camera>) {
    setFilters((previousFilters) => ({
      ...previousFilters,
      camera_ids: cameras.map(({ id }) => id),
    }));
    resetPagination();
  }

  function handleGatewaysChange(gateways: MultiValue<Gateway>) {
    setFilters((previousFilters) => ({
      ...previousFilters,
      gateway_ids: gateways.map(({ id }) => id),
    }));
    resetPagination();
  }

  function handleTrackableListsChange(lists: TrackableList[] = []) {
    const selections = [...lists];
    const selectedIds = selections.map(({ id }) => id);
    const filteredItems = selectedTrackableItems.filter((item) =>
      selectedIds.includes(item.trackable_list_id)
    );

    setSelectedTrackableLists(selections);
    setSelectedTrackableItems(filteredItems);

    setFilters((previousFilters) => {
      const currentEntities =
        previousFilters.search_entities ?? DEFAULT_SEARCH_ENTITIES;
      const nextEntities = selectedIds.length
        ? (Array.from(
            new Set<FilesSearchEntity>([...currentEntities, 'trackables'])
          ) as FilesSearchEntity[])
        : DEFAULT_SEARCH_ENTITIES;

      return {
        ...previousFilters,
        trackable_list_ids: selectedIds.length ? selectedIds : undefined,
        trackable_item_ids: filteredItems.length
          ? filteredItems.map(({ id }) => id)
          : undefined,
        search_entities: nextEntities,
        sort_by: selectedIds.length ? 'created_at' : previousFilters.sort_by,
      };
    });
    resetPagination();
  }

  function handleTrackableItemsChange(items: TrackableItem[] = []) {
    const selections = [...items];

    setSelectedTrackableItems(selections);

    setFilters((previousFilters) => {
      const currentEntities =
        previousFilters.search_entities ?? DEFAULT_SEARCH_ENTITIES;
      let nextEntities: FilesSearchEntity[];
      if (selections.length) {
        nextEntities = Array.from(
          new Set<FilesSearchEntity>([...currentEntities, 'trackables'])
        ) as FilesSearchEntity[];
      } else if (previousFilters.trackable_list_ids?.length) {
        nextEntities = currentEntities;
      } else {
        nextEntities = DEFAULT_SEARCH_ENTITIES;
      }

      return {
        ...previousFilters,
        trackable_item_ids: selections.length
          ? selections.map(({ id }) => id)
          : undefined,
        search_entities: nextEntities,
      };
    });
    resetPagination();
  }

  function handleSearchEntitiesChange(searchEntities: FilesSearchEntity[]) {
    const includesTrackables = searchEntities.includes('trackables');

    setFilters(({ confidence, ...previousFilters }) => {
      const nextFilters = {
        ...previousFilters,
        search_entities: searchEntities,
        // Remove confidence if vectors filter has been removed
        confidence: searchEntities.includes('vectors')
          ? confidence
          : undefined,
      };

      if (!includesTrackables) {
        nextFilters.trackable_list_ids = undefined;
        nextFilters.trackable_item_ids = undefined;
      }

      return nextFilters;
    });
    resetPagination();

    if (!includesTrackables) {
      setSelectedTrackableLists([]);
      setSelectedTrackableItems([]);
    }
  }

  const isImageSearchActive = Boolean(imageSearchEmbedding?.length && imageSearchType);

  function handleImageSearch(embedding: number[], type: ImageSearchType, sourceFile: File) {
    setImageSearchEmbedding(embedding);
    setImageSearchType(type);
    if (imageSearchPreviewUrl) URL.revokeObjectURL(imageSearchPreviewUrl);
    setImageSearchPreviewUrl(URL.createObjectURL(sourceFile));
    setSelectedTrackableLists([]);
    setSelectedTrackableItems([]);
    resetPagination();
    // Faces match at lower similarity than full person crops, so default
    // each search kind to a sensible starting relevance threshold.
    const defaultConfidence = type === 'face_reid' ? 0.3 : 0.8;
    setFilters((prev) => ({
      ...prev,
      query: undefined,
      trackable_list_ids: undefined,
      trackable_item_ids: undefined,
      sort_by: 'created_at',
      confidence: defaultConfidence,
    }));
  }

  function clearImageSearch() {
    setImageSearchEmbedding(undefined);
    setImageSearchType(undefined);
    if (imageSearchPreviewUrl) URL.revokeObjectURL(imageSearchPreviewUrl);
    setImageSearchPreviewUrl(undefined);
    resetPagination();
  }

  function handleSortChange(sort: string) {
    if (sort === 'created_at' || sort === 'relevance') {
      setFilters((previousFilters) => ({
        ...previousFilters,
        sort_by: sort,
      }));
      resetPagination();
    }
  }

  // Search (offset) pagination handlers
  function handleSearchNextPage() {
    const data = filesSearchQueryResult.data;
    if (!data || !data.next_page) return;

    setSearchPage(data.next_page);
  }

  function handleSearchPreviousPage() {
    const data = filesSearchQueryResult.data;
    if (!data || !data.previous_page) return;

    setSearchPage(data.previous_page);
  }

  const searchEntities = filters.search_entities;
  const isConfidenceSliderDisabled =
    !isImageSearchActive &&
    !(searchEntities.length && searchEntities.includes('vectors'));
  const canSearch = currentPlan && currentPlan.can_search;
  const shouldShowUpgradeNotice = !canSearch && isSearching;

  // Derive pagination props and data based on which path is active
  const queryResult =
    canSearch && isSearching ? filesSearchQueryResult : filesQueryResult;

  let listData: (FilesSearchResult | LumeoFileWithSearchMeta)[] = [];
  let paginationProps: CursorPaginationProps;

  if (canSearch && isSearching) {
    const data = filesSearchQueryResult.data;
    listData = data?.data ?? [];
    paginationProps = {
      page: searchPage,
      hasNextPage: Boolean(data?.next_page),
      hasPreviousPage: Boolean(data?.previous_page),
      onNextPage: handleSearchNextPage,
      onPreviousPage: handleSearchPreviousPage,
      disabled: filesSearchQueryResult.isFetching,
    };
  } else {
    listData = filesQueryResult.data?.data ?? [];
    paginationProps = getCursorPaginationProps(
      filesQueryResult.data,
      filesQueryResult.isFetching
    );
  }

  return (
    <>
      <div className={c('list')}>
        <div className={c('controls')}>
          <DateTimeRangeInput
            className={c('input')}
            value={dateRange}
            onChange={handleDateRangeChange}
            disabled={shouldShowUpgradeNotice}
          />
          <div className="flex items-center gap-2">
            <OptionalTooltip
              showTooltip={isImageSearchActive}
              content="Text search is disabled while Appearance search is active"
            >
              <Input
                icon="search"
                value={filters.query}
                className={c('input')}
                size="small"
                onChange={handleInputChange}
                placeholder="ex. `car`, `car.red`, `*ABC*` (wildcards), `person wearing white shirt`"
                disabled={isImageSearchActive}
              />
            </OptionalTooltip>
            {isImageSearchActive && (
              <Tooltip
                content={
                  imageSearchPreviewUrl ? (
                    <img
                      src={imageSearchPreviewUrl}
                      alt="Search image"
                      className="max-h-48 max-w-48 rounded"
                    />
                  ) : (
                    'Clear appearance search'
                  )
                }
                side="bottom"
              >
                <Button
                  variant="primary"
                  size="small"
                  onClick={clearImageSearch}
                  className="shrink-0"
                >
                  Clear appearance search
                </Button>
              </Tooltip>
            )}
          </div>
          {isLoadingStream ? (
            <Skeleton height="var(--size-xl)" width="100%" />
          ) : (
            <StreamSelect
              className={c('input')}
              size="small"
              defaultValue={stream}
              isLoading={isLoadingStream}
              onChange={handleStreamsChange}
              queryFilters={{
                sources: ['camera_stream', 'uri_stream'],
                stream_types: ['file', 'rtsp'],
              }}
              isDisabled={shouldShowUpgradeNotice}
              isMulti
            />
          )}
          <OptionalTooltip
            showTooltip={isConfidenceSliderDisabled}
            content="Enable scenes search to set confidence"
          >
            <FilesSearchConfidenceSlider
              value={filters.confidence ?? 0.5}
              onChangeEnd={handleConfidenceChange}
              disabled={
                isConfidenceSliderDisabled ||
                !isSearching ||
                shouldShowUpgradeNotice
              }
            />
          </OptionalTooltip>

          <Accordion
            className={c('input')}
            variant="flat"
            level="4"
            label="More filters"
          >
            <div className={c('selects')}>
              <CameraSelect
                size="small"
                onChange={handleCamerasChange}
                isDisabled={shouldShowUpgradeNotice}
                isMulti
              />
              <GatewaySelect
                size="small"
                onChange={handleGatewaysChange}
                isDisabled={shouldShowUpgradeNotice}
                isMulti
              />
              <TrackableListSelect<true>
                size="small"
                value={selectedTrackableLists}
                onChange={(lists) =>
                  handleTrackableListsChange(Array.isArray(lists) ? lists : [])
                }
                isMulti
                isClearable
                isDisabled={isImageSearchActive || shouldShowUpgradeNotice}
              />
              <TrackableItemSelect<true>
                size="small"
                value={selectedTrackableItems}
                onChange={(items) =>
                  handleTrackableItemsChange(Array.isArray(items) ? items : [])
                }
                trackableLists={selectedTrackableLists}
                isMulti
                isClearable
                isDisabled={
                  isImageSearchActive || shouldShowUpgradeNotice || !hasSelectedTrackableLists
                }
              />
            </div>

            <OptionalTooltip
              showTooltip={isImageSearchActive}
              content="Text search is disabled while Appearance search is active"
            >
              <Field className={c('field')} label="Search by">
                <FilesSearchEntitiesInput
                  id="files-search-entities-input-drawer"
                  defaultValue={DEFAULT_SEARCH_ENTITIES}
                  onChange={handleSearchEntitiesChange}
                  disabled={isImageSearchActive || !isSearching || shouldShowUpgradeNotice}
                  value={filters.search_entities}
                />
              </Field>
            </OptionalTooltip>
            <Field className={c('field')} label="Sort by">
              <Radio
                id="search_sort"
                value={filters.sort_by}
                onChange={(value) =>
                  value && typeof value === 'string' && handleSortChange(value)
                }
                disabled={!isSearching || shouldShowUpgradeNotice}
              >
                <RadioOption value="relevance">Relevance</RadioOption>
                <RadioOption value="created_at">Date</RadioOption>
              </Radio>
            </Field>
          </Accordion>
        </div>

        {!shouldShowUpgradeNotice && (
          <FilesSearchDrawerList
            data={listData}
            isLoading={queryResult.isLoading}
            isFetching={queryResult.isFetching}
            isPreviousData={queryResult.isPreviousData ?? false}
            onFileClick={setSelectedFileId}
            selectedFileId={selectedFileId}
            {...paginationProps}
          />
        )}
      </div>

      <div className={c('detail')}>
        {shouldShowUpgradeNotice && (
          <div className={c('empty')}>
            <FilesSearchUpgradeNotice
              onCancel={() => {
                setFilters(({ ...previousFilters }) => ({
                  ...previousFilters,
                  query: '',
                  sort_by: 'created_at',
                }));
                resetPagination();
              }}
            />
          </div>
        )}

        {!selectedFileId && !shouldShowUpgradeNotice && (
          <div className={c('empty')}>Please select a file from the list.</div>
        )}

        {selectedFileId && !shouldShowUpgradeNotice && (
          <FilesSearchDetail key={selectedFileId} id={selectedFileId} onImageSearch={handleImageSearch} />
        )}
      </div>
    </>
  );
}
